import React from 'react';
import { css } from '@emotion/react';
import Caret from './caret';
import NavItem from './nav-item';
import * as NavigationMenu from '../../primitives/navigation-menu';
import { atTabletBreakpoint, getColumnWidthAndOffset, getHeaderContentWidth } from './utils';
import Container from '../../primitives/grid/container';
import FirstLevelChild from './first-level-child';
import { MenuNavItem } from '../types';

const styles = {
    navItem: css({
        height: '100%',
        display: 'flex',
        alignItems: 'center',
    }),
    trigger: css({
        display: 'flex',
        select: 'none',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
    }),
    caret: css({
        height: '5px',
        marginBottom: '4px',
        marginLeft: '5px',
    }),
    content: css({
        position: 'absolute',
        top: '0',
        left: '0',
        width: 'auto',
    }),
    outerContainer: css({
        margin: '0',
        display: 'grid',
        listStyleType: 'none',
        width: 'var(--nav-standard-width)',
        ...atTabletBreakpoint({
            width: 'var(--nav-collapsed-width)',
        }),
    }),
    innerContainer: css({
        display: 'flex',
        justifyContent: 'center',
    }),
    listContainer: css({
        margin: '38px 0',
        width: '100%',
        maxWidth: '1193px',
        /*
        * 1313 - 60 * 2 (largest margin) = 1193
        * This is being done so that when the dropdown
        * is full size, the margin aligns with the logo
        * and lower content.
        * */
        ...atTabletBreakpoint({
            margin: '28px 0',
        }),
    }),
    list: css({
        display: 'flex',
        flexFlow: 'wrap',
        width: '100%',
        columnGap: '40px',
        rowGap: '47px',
        ...atTabletBreakpoint({
            rowGap: '15px',
            columnGap: '33px',
        }),
    }),
    firstLevelChild: css({
        width: 'var(--child-standard-width)',
        ...atTabletBreakpoint({
            width: 'var(--child-collapsed-width)',
        }),
    }),
};

const MenuItem = ({ item }: { item: MenuNavItem }): JSX.Element => {
    const { navItemLink, columns = [] } = item;
    const { href, title } = navItemLink;

    const childMenu = columns.flatMap(x => x.group).filter(x => !!x);

    if (!(childMenu.length > 0)) {
        return (
            <NavItem
                to={href}
                css={styles.navItem}
            >
                {title}
            </NavItem>
        );
    }

    return (
        <>
            <NavigationMenu.Trigger
                css={styles.trigger}
            >
                <NavItem>
                    {title}
                    <Caret css={styles.caret} />
                </NavItem>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content
                animationTimeMs={400}
                css={styles.content}
                mode={childMenu.length > 2 ? 'center' : 'self'}
            >
                <div
                    css={styles.outerContainer}
                    style={{
                        '--nav-standard-width': getHeaderContentWidth(1313, childMenu.length, 4),
                        '--nav-collapsed-width': getHeaderContentWidth(1313, childMenu.length, 2),
                    } as React.CSSProperties}
                >
                    <Container
                        canStack
                        css={styles.innerContainer}
                    >
                        <div css={styles.listContainer}>
                            <ul css={styles.list}>
                                {childMenu.map((node) => {
                                    const childSubgroupLength = (childMenu || []).filter(x => !!x).map((x) => {
                                        if (x.subgroup && x.subgroup.filter(x => !!x).length > 0) {
                                            return x.subgroup.filter(x => !!x).length;
                                        }
                                        return 1;
                                    }).reduce((acc, val) => acc + val, 0);

                                    const {
                                        widthPercent,
                                        offsetPixels,
                                    } = getColumnWidthAndOffset(
                                        Math.min(Math.max(childSubgroupLength, 1), 4),
                                        40,
                                        node.subgroup.length,
                                    );

                                    const {
                                        widthPercent: widthPercentTb,
                                        offsetPixels: offsetPixelsTb,
                                    } = getColumnWidthAndOffset(
                                        Math.min(Math.max(childSubgroupLength, 2), 2),
                                        40,
                                        node.subgroup.length,
                                    );

                                    return (
                                        <div
                                            css={styles.firstLevelChild}
                                            style={{
                                                '--child-standard-width': `calc(${widthPercent}% - ${offsetPixels}px)`,
                                                '--child-collapsed-width': `calc(${widthPercentTb}% - ${offsetPixelsTb}px)`,
                                            } as React.CSSProperties}
                                            key={node.categoryLink.title}
                                        >
                                            <FirstLevelChild item={node} />
                                        </div>
                                    );
                                })}
                            </ul>
                        </div>
                    </Container>
                </div>
            </NavigationMenu.Content>
        </>
    );
};

export default MenuItem;
